<template>
  <v-container fluid class="jobs-view py-0" :fill-height="loading">

    <v-system-bar v-if="preview" color="warning">{{ $t('public_job_ads.this_is_preview') }}</v-system-bar>

    <v-progress-linear style="position: fixed; left: 0; right: 0; margin: 0; z-index: 99;"
                       :style="{top: $vuetify.breakpoint.smAndDown ? '54px' : '62px'}" color="primary lighten-2"
                       height="2" v-show="subtleLoading" :indeterminate="true"></v-progress-linear>

    <hb-loading-indicator v-if="loading" align-middle/>

    <v-fade-transition>
      <v-row v-if="!loading" style="position: relative;">
        <v-col cols="12" v-if="isTestResultsView" md="10" offset-md="1" xl="8" offset-xl="2">
          <div v-if="jobs.length > 0" class="font-weight-bold pt-3 pl-6">{{ $t('public_job_ads.jobs_for_you') }}:</div>
          <div v-else class="font-weight-bold pt-3">
            <v-alert :value="true" color="accent" class="mb-0 pb-0 px-6">
              {{ $t('public_job_ads.no_jobs_for_you') }}
              <br />
              <a class="py-3 d-inline-block white--text" @click="showAllJobs">
                {{ $t('public_job_ads.check_other_jobs') }}
              </a>
            </v-alert>
          </div>
        </v-col>
        <template v-if="jobs.length > 0">
          <v-col cols="12" v-if="!isTestResultsView && testView === '1'" md="10" offset-md="1" xl="8" offset-xl="2" class="pb-0">
            <v-btn color="accent-3"
                   text
                   block
                   @click="backToTestResults">
              {{ $t('public_job_ads.back_to_test_results') }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="10" offset-md="1" xl="8" offset-xl="2" class="pb-0">
            <v-expansion-panels flat accordion class="jobs-list" :value="selectedPanel" multiple>
              <template v-for="job in jobs">
                <JobExpansionPanel :key="job.id" :job="job" @apply-job="applyJob" @set-url="setUrl" />
              </template>
            </v-expansion-panels>
          </v-col>

          <v-col v-if="isTestResultsView" cols="12" md="10" offset-md="1" xl="8" offset-xl="2">
            <v-btn color="accent-3"
                   text
                   block
                   @click="showAllJobs">
              {{ $t('public_job_ads.check_other_jobs') }}
            </v-btn>
          </v-col>
        </template>

        <v-col cols="12" md="10" offset-md="1" xl="8" offset-xl="2" v-if="openApplication">
          <v-card color="accent-2">
            <v-container fill-height pa-6 fluid>
              <v-row>
                <v-col cols="12" class="pt-16">
                  <div style="position: relative;">
                    <v-img :src="$assetImageUrl + 'img/face-3.svg'" :width="$vuetify.breakpoint.xsOnly ? 65 : $vuetify.breakpoint.xl ? 170 : 120" class="face"/>
                    <div class="title mb-5">{{ $t('public_job_ads.no_match') }}</div>
                    <div class="headline mb-5">
                      {{ $t('public_job_ads.leave_open_application') }}<br />
                      <span class="font-weight-regular">{{ $t('public_job_ads.so_we_can_contact_you') }}</span>
                    </div>
                    <v-btn color="primary" :block="$vuetify.breakpoint.smAndDown" class="accent-2--text" @click="leaveOpenApplication">{{ $t('public_job_ads.leave_open_application') }}</v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

  </v-container>
</template>

<script>

import sharedApi from '@/jobs/api/shared';
import OnlineTestService from '@/jobs/service/onlinetest.service';
import JobExpansionPanel from "@/jobs/components/JobExpansionPanel";

export default {
  name: "JobsView",
  components: {
    JobExpansionPanel
  },
  props: {
    testView: {
      type: String,
      default: '0',
    },
    selectedJobId: {
      type: [String, Number],
      default: -1,
    },
  },
  data() {
    return {
      loading: true,
      subtleLoading: false,
      jobs: [],
      selectedPanel: [],
      isTestResultsView: false,
      openApplication: null,
      preview: undefined,
    }
  },
  watch: {
    isTestResultsView() {
      this.reloadJobs();
    }
  },
  methods: {
    leaveOpenApplication() {
      this.applyJob(this.openApplication.id);
    },
    applyJob(id) {
      this.$router.push({name: 'apply_job', params: { jobId: id }, query: { preview: this.preview } })
    },
    showAllJobs() {
      this.isTestResultsView = false;
    },
    backToTestResults() {
      if (this.testView === '1') {
        this.isTestResultsView = true;
      }
    },
    setUrl(id) {
      if (parseInt(this.selectedJobId) === parseInt(id)) {
        this.$router.push({ name: 'jobs', params: { testView: this.testView === '1' ? '1' : '0' }, query: { preview: this.preview } });
      } else {
        this.$router.push({name: 'job', params: { selectedJobId: id, testView: this.testView }, query: { preview: this.preview }});
      }
    },
    async reloadJobs() {
      this.loading = true;
      try {
        if (this.preview) {
          this.jobs = [ await sharedApi.getJobAdPreview(this.preview) ];
        } else if (this.isTestResultsView) {
          this.jobs = await sharedApi.getJobAds(OnlineTestService.getAnswers());
        } else {
          this.jobs = await sharedApi.getJobAds();
        }
        if (!this.preview) {
          this.openApplication = await sharedApi.getOpenApplication();
        }
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loading = false;
    },
  },
  async mounted() {
    this.loading = false;
    this.isTestResultsView = this.testView === '1';
    this.preview = this.$route.query.preview;

    await this.reloadJobs();
    if (this.selectedJobId >= 0) {
      this.selectedPanel = [this.jobs.findIndex(j => j.id === parseInt(this.selectedJobId))];
      if (!this.preview) {
        setTimeout(() => {
          this.$vuetify.goTo(`#job_${this.selectedJobId}`, { duration: 200, offset: 12, easing: 'easeInOutCubic' });
        }, 1000);
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.face {
  position: absolute;
  top: -60px;
  right: 20px;
}
@media only screen and (min-width: 1200px) {
  .face {
    top: -30px;
    right: 60px;
  }
}
</style>

<style lang="scss">
.jobs-list {
  .v-expansion-panel-content__wrap {
    padding: 0;
  }
}

@media only screen and (min-width: 960px) {
  .jobs-list {
    .v-expansion-panel-content__wrap {
      padding: 16px 24px;
    }
  }
}
</style>
